import type { CompiledChart } from '../../songpack/index.js';

/**
 * When a wave *wants* to be tapped.
 *
 * ## What the old build actually did
 *
 * Unity had no such thing as a per-wave press time. Its SAFE prompt appeared
 * only after the player physically crossed the wave's gate, and
 * `CircularWave.ResolveSafeActivationTime` then snapped that crossing to **the
 * first detected beat at or after it, plus `beatOffsetSeconds`** (0.08 in the
 * shipped `SafeRhythmSettings`), falling back to a BPM grid built from
 * `firstBeatOffsetSeconds` when a chart carried no beat list.
 *
 * So the moment was beat-aligned, but *which* beat depended on where the player
 * happened to be. With no player, that definition cannot be carried over — it
 * has no value to compute. The new rule needs a time that is a pure function of
 * the chart.
 *
 * ## What this uses instead, and why
 *
 * The anchor moves from "the player crossed" to **the wave's own expansion
 * start** — `event.time + warningLeadTime` — and Unity's snapping is applied to
 * that unchanged. Three things make this the closest available reading of the
 * old behaviour rather than a new invention:
 *
 * 1. It keeps the beat alignment. Measured against the real pack, every one of
 *    the 127/278/508 events already sits *exactly* on a detected beat (largest
 *    deviation across all of them: 0.0000 s), so the chart's own grid is the
 *    musical grid, and snapping to it lands on the music.
 * 2. It keeps the authored 0.08 s offset, which is the one number in the old
 *    settings that was tuned by ear rather than derived.
 * 3. It lands after the warning window rather than during it. `warningLeadTime`
 *    is 0.8 s throughout the real pack, and the requirement document calls that
 *    window "発生前予告" — advance notice. Making the best moment fall inside
 *    the notice would mean the ideal tap happens before the player has been
 *    told anything.
 *
 * On this pack the result is `event.time + ~0.996 s`: the wave appears, the
 * player gets the warning window plus a beat to read it, and the best tap lands
 * on the second beat after the event. Waves then linger well past their best
 * moment, which is intended — the new rule is that a late tap still works.
 */

export interface BestTimeSettings {
  /**
   * Seconds added to the beat. Unity's `SafeRhythmSettings.beatOffsetSeconds`,
   * shipped at 0.08.
   */
  readonly beatOffsetSeconds: number;
  /** Use the chart's detected beats before falling back to a BPM grid. */
  readonly preferDetectedBeats: boolean;
  /** Beats are divided this many times when falling back to the grid. */
  readonly beatSubdivision: number;
  /** Used only when neither the chart nor the manifest supplies a BPM. */
  readonly fallbackBpm: number;
}

export const DEFAULT_BEST_TIME_SETTINGS: BestTimeSettings = Object.freeze({
  beatOffsetSeconds: 0.08,
  preferDetectedBeats: true,
  beatSubdivision: 1,
  fallbackBpm: 120,
});

/**
 * The first beat-aligned moment at or after `anchorTime`.
 *
 * Ported from `CircularWave.ResolveSafeActivationTime`, including its fallback
 * order: detected beats, then the chart's own event times, then a BPM grid. The
 * result is never earlier than the anchor.
 */
export function resolveBestTime(
  chart: CompiledChart,
  anchorTime: number,
  settings: BestTimeSettings = DEFAULT_BEST_TIME_SETTINGS,
): number {
  const offset = Number.isFinite(settings.beatOffsetSeconds) ? settings.beatOffsetSeconds : 0.08;

  if (settings.preferDetectedBeats) {
    const threshold = anchorTime - offset;
    const fromBeats = firstAscendingAtOrAfter(chart.detectedBeatTimes, threshold);
    if (fromBeats !== null) return fromBeats + offset;

    // Charts predating `detectedBeatTimes` still place events on editor-detected
    // musical moments, so they preserve rhythm alignment. Unity says the same.
    // Scanned rather than searched: event order is a chart property the compiler
    // reports but does not enforce, and a binary search over an unsorted array
    // would return a wrong answer instead of no answer.
    const fromEvents = smallestAtOrAfter(chart.events.time, threshold, chart.events.count);
    if (fromEvents !== null) return fromEvents + offset;
  }

  let bpm = chart.detectedBpm > 0 ? chart.detectedBpm : chart.bpm;
  if (!Number.isFinite(bpm) || bpm <= 0) bpm = settings.fallbackBpm;
  bpm = Math.max(1, bpm);
  const subdivision = Math.max(1, Math.floor(settings.beatSubdivision));
  const interval = 60 / bpm / subdivision;
  const origin = Number.isFinite(chart.firstBeatOffsetSeconds) ? chart.firstBeatOffsetSeconds : 0;
  const index = Math.ceil((anchorTime - offset - origin) / interval);
  return Math.max(anchorTime, origin + Math.max(0, index) * interval + offset);
}

/**
 * First value at or after `threshold` in an ascending array, or null.
 *
 * Binary search rather than a scan: this runs once per spawn against a beat list
 * that is 508 entries on the real pack, and a scan there would be 508 reads per
 * wave for a number that never changes.
 */
function firstAscendingAtOrAfter(values: ArrayLike<number>, threshold: number): number | null {
  let low = 0;
  let high = values.length;
  while (low < high) {
    const mid = (low + high) >>> 1;
    if ((values[mid] as number) < threshold) low = mid + 1;
    else high = mid;
  }
  for (let index = low; index < values.length; index += 1) {
    const value = values[index] as number;
    // Skip a stray negative or NaN rather than returning it; the list is sorted
    // so the next candidate is still the earliest valid one.
    if (Number.isFinite(value) && value >= 0) return value;
  }
  return null;
}

/** Smallest value at or after `threshold`, scanning. Order-independent. */
function smallestAtOrAfter(
  values: ArrayLike<number>,
  threshold: number,
  count: number,
): number | null {
  let best = Number.POSITIVE_INFINITY;
  const limit = Math.min(count, values.length);
  for (let index = 0; index < limit; index += 1) {
    const value = values[index] as number;
    if (!Number.isFinite(value) || value < 0) continue;
    if (value >= threshold && value < best) best = value;
  }
  return best === Number.POSITIVE_INFINITY ? null : best;
}
